import React from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { Link } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { useCurrentUser } from "../context/AuthContext";
import { nameFor } from "../utils/displayName";

const MY_THREADS = gql`
  query MyThreads {
    myThreads {
      nodes {
        id
        bookingId
        guestId
        hostId
        lastMessageAt
        guestLastReadAt
        hostLastReadAt
        bookingByBookingId {
          startAt endAt status
          saunaBySaunaId { id name }
        }
        userByGuestId { userProfileByUserId { fullName displayName } }
        userByHostId  { userProfileByUserId { fullName displayName } }
      }
    }
  }
`;

function displayName(user: { userProfileByUserId?: { fullName?: string; displayName?: string | null } | null }) {
  return nameFor(user.userProfileByUserId, "User");
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Messages() {
  usePageTitle("Messages — Lets Go Sauna");
  const currentUserId = useCurrentUser();
  const { data, loading, error } = useQuery(MY_THREADS, { fetchPolicy: "network-only" });

  if (loading) return <p style={{ padding: 24 }}>Loading...</p>;
  if (error)   return <p style={{ padding: 24, color: "#ef4444" }}>Error: {error.message}</p>;

  const threads = data?.myThreads?.nodes ?? [];

  return (
    <div style={{ maxWidth: 640, margin: "40px auto", padding: "0 24px 60px" }}>
      <h2 style={{ marginTop: 0, marginBottom: 24 }}>Messages</h2>

      {threads.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: 14 }}>No conversations yet. Messages appear here once you book a sauna.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {threads.map((t: any) => {
            const isGuest    = currentUserId === t.guestId;
            const counterpart = isGuest ? t.userByHostId : t.userByGuestId;
            const myLastRead = isGuest ? t.guestLastReadAt : t.hostLastReadAt;
            const hasUnread  = t.lastMessageAt && new Date(t.lastMessageAt) > new Date(myLastRead);
            const sauna      = t.bookingByBookingId?.saunaBySaunaId;
            const booking    = t.bookingByBookingId;

            return (
              <Link
                key={t.id}
                to={`/messages/${t.id}`}
                style={threadRow(hasUnread)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {hasUnread && <span style={unreadDot} />}
                    <span style={{ fontWeight: hasUnread ? 700 : 500, fontSize: 15, color: "#111827" }}>
                      {counterpart ? displayName(counterpart) : "Unknown"}
                    </span>
                  </div>
                  {t.lastMessageAt && (
                    <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0, marginLeft: 8 }}>
                      {fmtDate(t.lastMessageAt)}
                    </span>
                  )}
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                  {sauna?.name} · {booking ? fmtDate(booking.startAt) : ""}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

const threadRow = (unread: boolean): React.CSSProperties => ({
  display: "block",
  padding: "14px 16px",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  marginBottom: 8,
  textDecoration: "none",
  background: unread ? "#eff6ff" : "#fff",
  transition: "background 0.1s",
});

const unreadDot: React.CSSProperties = {
  width: 8, height: 8, borderRadius: "50%",
  background: "#2563eb", flexShrink: 0,
};
